import * as React from 'react';
import { motion } from 'framer-motion';

import { SuggestionCardRail } from './SuggestionCardRail';

const SCROLL_INTO_VIEW_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
  inline: 'nearest',
};

function escapeSuggestionId(suggestionId: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(suggestionId) : suggestionId;
}

function findSuggestionAnchor(root: HTMLElement, suggestionId: string): HTMLElement | null {
  const escapedId = escapeSuggestionId(suggestionId);
  const preferredSelector = `.review-suggestion.inline-add[data-review-suggestion-id="${escapedId}"]`;
  const fallbackSelector = `[data-review-suggestion-id="${escapedId}"]`;

  return root.querySelector<HTMLElement>(preferredSelector) ?? root.querySelector<HTMLElement>(fallbackSelector);
}

function scrollSuggestionIntoView(root: HTMLElement, suggestionId: string): boolean {
  const anchor = findSuggestionAnchor(root, suggestionId);
  if (!anchor || !anchor.isConnected) return false;
  anchor.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);

  return true;
}

export interface ReviewSuggestionsViewportProps {
  className?: string;
  containerRef: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
  selectedReviewSuggestionId?: string | null;
  onReviewSuggestionSelect?: (suggestionId: string | null) => void;
  onReviewSuggestionAccept?: (suggestionId: string) => Promise<void> | void;
  onReviewSuggestionReject?: (suggestionId: string) => Promise<void> | void;
  onReviewSuggestionSourceClick?: (sourceId: string) => void;
  readOnly?: boolean;
  reviewSuggestionRailTopClassName?: string;
}

/**
 * Owns editor+suggestion layout concerns for the right rail,
 * so EditorCore can focus on Milkdown lifecycle and plugin wiring.
 */
export const ReviewSuggestionsViewport: React.FC<ReviewSuggestionsViewportProps> = ({
  className,
  containerRef,
  children,
  selectedReviewSuggestionId,
  onReviewSuggestionSelect,
  onReviewSuggestionAccept,
  onReviewSuggestionReject,
  onReviewSuggestionSourceClick,
  readOnly,
  reviewSuggestionRailTopClassName = 'top-0',
}) => {
  // Dedicated DOM ref for the Milkdown pane so review-suggestion DOM queries
  // only scan editor decorations.
  const editorPaneDomRef = React.useRef<HTMLDivElement | null>(null);
  const showSuggestionRail = Boolean(selectedReviewSuggestionId);
  const rootClassName = [
    className,
    'relative flex items-start overflow-visible',
    showSuggestionRail ? 'gap-4' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  React.useEffect(() => {
    if (!selectedReviewSuggestionId) return undefined;

    const root = editorPaneDomRef.current;
    if (!root) return undefined;

    if (scrollSuggestionIntoView(root, selectedReviewSuggestionId)) return undefined;

    const raf = requestAnimationFrame(() => {
      scrollSuggestionIntoView(root, selectedReviewSuggestionId);
    });

    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (scrollSuggestionIntoView(root, selectedReviewSuggestionId)) observer?.disconnect();
      });
      observer.observe(root, { subtree: true, childList: true, attributes: true });
    }

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [selectedReviewSuggestionId]);

  return (
    <div ref={containerRef} className={rootClassName} data-testid="brainfish-editor">
      <div ref={editorPaneDomRef} className="min-w-0 flex-1">
        {children}
      </div>

      <aside className={`sticky ${reviewSuggestionRailTopClassName} self-start shrink-0`}>
        <motion.div
          initial={{ width: 0 }} // re-open animates from collapsed on revisit
          animate={{ width: showSuggestionRail ? 320 : 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 30, mass: 0.9 }}
          className="overflow-visible"
        >
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: showSuggestionRail ? 1 : 0, x: showSuggestionRail ? 0 : 12 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={showSuggestionRail ? 'w-80 px-3 py-2' : 'pointer-events-none w-80 px-3 py-2'}
          >
            <SuggestionCardRail
              selectedReviewSuggestionId={selectedReviewSuggestionId}
              onReviewSuggestionSelect={onReviewSuggestionSelect}
              onReviewSuggestionAccept={onReviewSuggestionAccept}
              onReviewSuggestionReject={onReviewSuggestionReject}
              onReviewSuggestionSourceClick={onReviewSuggestionSourceClick}
              editorRootRef={editorPaneDomRef}
              readOnly={readOnly}
            />
          </motion.div>
        </motion.div>
      </aside>
    </div>
  );
};
